import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
    getToken,
    getUsername,
    getUserId,
    getUserName,
    getUserRole,
    getClaims,
    isTokenExpired,
    loginPersist,
    logout as logoutPersist,
} from "../utils/auth";

const AuthContext = createContext(null);

function readSnapshot() {
    const token = getToken();
    if (!token || isTokenExpired()) {
        return { token: null, user: null };
    }
    return {
        token,
        user: {
            id: getUserId(),
            username: getUsername(),
            name: getUserName(),
            role: getUserRole(),
            claims: getClaims(),
        },
    };
}

export function AuthProvider({ children }) {
    const [state, setState] = useState(readSnapshot);
    const navigate = useNavigate();

    const refresh = useCallback(() => {
        setState(readSnapshot());
    }, []);

    const login = useCallback((token, username) => {
        loginPersist(token, username);
        setState(readSnapshot());
    }, []);

    const logout = useCallback(() => {
        logoutPersist();
        setState({ token: null, user: null });
    }, []);

    // Listen for 401 events dispatched by the API layer
    useEffect(() => {
        const handler = () => {
            logoutPersist();
            setState({ token: null, user: null });
            const next = encodeURIComponent(window.location.pathname);
            navigate(`/login?next=${next}`, { replace: true });
        };
        window.addEventListener("auth:expired", handler);
        return () => window.removeEventListener("auth:expired", handler);
    }, [navigate]);

    // Periodically check token expiry (every 60s)
    useEffect(() => {
        if (!state.token) return undefined;
        const id = window.setInterval(() => {
            if (isTokenExpired()) {
                window.dispatchEvent(new CustomEvent("auth:expired"));
            }
        }, 60_000);
        return () => window.clearInterval(id);
    }, [state.token]);

    const value = useMemo(
        () => ({
            token: state.token,
            user: state.user,
            isAuthenticated: !!state.token,
            login,
            logout,
            refresh,
        }),
        [state, login, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
